import type { OrderDetails } from '@/entities/order/model'
import { getRestricted } from '@/entities/order/lib'
import { captureMessage } from '@/shared/lib/sentry'

/**
 * Determines the display value for restricted items based on the order's hsFields
 *
 * Business rules:
 * - If received 1 (or "1"), display "Yes"
 * - If received 0 (or "0"), display "No"
 * - If missing, undefined, null, or empty string, display "No"
 * - For any other defined value, display "No" and emit a warning to Sentry
 *
 * @param hsFields - The hsFields object from order details
 * @returns "Yes" or "No" based on the restricted items value
 */
export function getRestrictedItemsDisplay(hsFields?: OrderDetails['hsFields']): string {
  if (!hsFields) {
    return 'No'
  }

  const restrictedValue = getRestricted(hsFields)

  if (restrictedValue === undefined || restrictedValue === null) {
    return 'No'
  }

  const normalized = String(restrictedValue).trim()

  if (normalized === '') {
    return 'No'
  }

  if (normalized === '1') {
    return 'Yes'
  }

  if (normalized === '0') {
    return 'No'
  }

  // Any other defined value is unexpected, log warning to Sentry and return 'No'
  captureMessage('Unexpected Restricted field value in order', 'warning', { value: restrictedValue })
  return 'No'
}

/**
 * Predicate to check if the order has restricted items data present
 *
 * @param order - The order details object
 * @returns true if Restricted field is present and non-empty, false otherwise
 */
export function hasRestrictedField(order?: OrderDetails): boolean {
  if (!order || !order.hsFields) {
    return false
  }

  const restrictedValue = getRestricted(order.hsFields)

  return restrictedValue !== undefined && restrictedValue !== null && String(restrictedValue).trim() !== ''
}
